import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { signUp } from '../lib/api-client';
import './create-account.scss';
import { defaultDivisions } from '../constants';

const CreateAccountContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const CreateAccountCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 420,
  width: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: '#667eea',
    },
  },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
    borderColor: '#667eea',
  },
}));

const CreateAccountButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  fontWeight: 600,
  '&:hover': {
    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  background: '#2c2c2c',
  color: '#f5f5f5',
  textTransform: 'none',
  minWidth: '60px',
  '&:hover': {
    background: '#1a1a1a',
  },
}));

export const CreateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    wsdcId: '',
    division: '',
    role: '',
    location: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [divisions, setDivisions] = useState<string[]>([]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleAddDivision = () => {
    if (formData.division && formData.role) {
      setDivisions([...divisions, `${formData.division} - ${formData.role}`]);
      setFormData({ ...formData, division: '', role: '' });
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validation
      if (!formData.name || !formData.email || !formData.username || !formData.password) {
        throw new Error('Please fill in all required fields');
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      // Create account via server (handles Firebase Auth + Firestore profile)
      const divisionsData = divisions.map((div) => {
        const [division, role] = div.split(' - ');
        return { division, role };
      });

      const user = await signUp(
        formData.email,
        formData.password,
        formData.name,
        formData.username,
        formData.wsdcId || undefined,
        formData.location || undefined,
        divisionsData
      );

      console.log('Account created successfully:', user);
      navigate('/profile');
    } catch (err: any) {
      console.error('Create account error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <CreateAccountContainer maxWidth="sm">
      <CreateAccountCard>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Create Account
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Join WeMove and start your dance journey
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Create Account Form */}
        <form onSubmit={handleCreateAccount}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Name Field */}
            <StyledTextField
              fullWidth
              label="Name"
              type="text"
              value={formData.name}
              onChange={handleChange('name')}
              disabled={loading}
              placeholder="Your full name"
              variant="outlined"
              required
            />

            {/* Email Field */}
            <StyledTextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              disabled={loading}
              placeholder="you@example.com"
              variant="outlined"
              required
            />

            {/* Username Field */}
            <StyledTextField
              fullWidth
              label="Username"
              type="text"
              value={formData.username}
              onChange={handleChange('username')}
              disabled={loading}
              placeholder="Choose a username"
              variant="outlined"
              required
            />

            {/* Password Field */}
            <StyledTextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange('password')}
              disabled={loading}
              placeholder="Enter your password"
              variant="outlined"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Confirm Password Field */}
            <StyledTextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              disabled={loading}
              placeholder="Re-enter your password"
              variant="outlined"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                      disabled={loading}
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* WSDC ID (optional) */}
            <StyledTextField
              fullWidth
              label="WSDC ID (optional)"
              type="text"
              value={formData.wsdcId}
              onChange={handleChange('wsdcId')}
              disabled={loading}
              placeholder="Optional"
              variant="outlined"
            />

            {/* Location (optional) */}
            <StyledTextField
              fullWidth
              label="Location (optional)"
              type="text"
              value={formData.location}
              onChange={handleChange('location')}
              disabled={loading}
              placeholder="Optional"
              variant="outlined"
            />

            {/* Add Divisions Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Add Divisions
              </Typography>

              {/* Display added divisions */}
              {divisions.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {divisions.map((div, index) => (
                    <Box
                      key={index}
                      sx={{
                        px: 2,
                        py: 0.5,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        fontSize: '0.875rem',
                      }}
                    >
                      {div}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Division Select */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select a Division</InputLabel>
                <Select
                  value={formData.division}
                  onChange={(e) => setFormData({ ...formData, division: e.target.value })}
                  disabled={loading}
                  label="Select a Division"
                >
                  {defaultDivisions.map((division) => (
                    <MenuItem key={division} value={division}>
                      {division}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Role Select with Add Button */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <FormControl fullWidth>
                  <InputLabel>Select a Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    disabled={loading}
                    label="Select a Role"
                  >
                    <MenuItem value="Leader">Leader</MenuItem>
                    <MenuItem value="Follower">Follower</MenuItem>
                    <MenuItem value="Both">Both</MenuItem>
                  </Select>
                </FormControl>
                <AddButton
                  variant="contained"
                  onClick={handleAddDivision}
                  disabled={!formData.division || !formData.role || loading}
                  sx={{ minHeight: 56 }}
                >
                  Add
                </AddButton>
              </Box>
            </Box>

            {/* Create Account Button */}
            <CreateAccountButton
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
            </CreateAccountButton>
          </Box>
        </form>

        {/* Sign In Link */}
        <Box sx={{ mt: 4, textAlign: 'center', borderTop: '1px solid #eee', pt: 3 }}>
          <Typography variant="body2" color="textSecondary">
            Already have an account?{' '}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                navigate('/sign-in');
              }}
              sx={{
                color: '#667eea',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </CreateAccountCard>
    </CreateAccountContainer>
  );
};
